import React, { useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { Location } from 'history'
import { DefaultTheme, ThemeProvider } from 'styled-components'
import { transparentize } from 'polished'

// Constants
import { ProfessionalRole, routes } from '@sketch/constants'

// Components
import IndexLayout, { Variant as SidebarVariant } from 'components/IndexLayout'
import { Link, useForDesktop } from '@sketch/components'
import RedirectBadge from 'components/RedirectBadge'
import SignUpForm from 'components/SignUpForm'

// Utils
import { isMac } from '@sketch/utils'
import { gqlTintToLocalTint } from 'modules/signUp/graphql/utils'

// GraphQL
import { SignUpContentFragment } from '@sketch/gql-types'

// Styles
import {
  Feature,
  FeatureImg,
  FullWidthImage,
  Line,
  OperatingSystemWarning,
  RelatedContentImg,
  RelatedContentLink,
  RelatedContentTitle,
  RelatedContentDescription,
  StyledColumn,
  StyledGrid,
  Subtitle,
  TextSignUp as Text,
  Title,
} from './SignUpView.styles'
import { tintColors } from '@sketch/global-styles'

export interface BaseSignUpViewProps {
  content: SignUpContentFragment
  email: string | undefined
  professionalRole: ProfessionalRole | undefined
  invitedUser: boolean
  onCompleted: () => void
  onRoleChange?: (role: ProfessionalRole) => void
}

const RelatedContent: React.FC<{
  content: BaseSignUpViewProps['content']
}> = ({ content }) => {
  const { relatedContent } = content
  if (!relatedContent) {
    return null
  }

  return (
    <RelatedContentLink target="_blank" href={relatedContent.url} external>
      <RelatedContentImg
        src={relatedContent.thumbnail.src}
        alt={relatedContent.thumbnail.alt}
      />
      <div>
        <RelatedContentTitle>{relatedContent.title}</RelatedContentTitle>
        <RelatedContentDescription>
          {relatedContent.description}
        </RelatedContentDescription>
      </div>
    </RelatedContentLink>
  )
}

const HeroImage: React.FC<{
  content: BaseSignUpViewProps['content']
}> = ({ content }) => {
  const { heroImage } = content
  if (!heroImage) {
    return null
  }
  return <FullWidthImage src={heroImage.src} alt={heroImage.alt} />
}

const gutter = {
  xl: 64,
  lg: 64,
  md: 40,
  sm: 32,
  xs: 16,
}

const BaseSignUpView: React.FC<BaseSignUpViewProps> = ({
  content,
  email,
  professionalRole,
  invitedUser,
  onCompleted,
  onRoleChange = () => {},
}) => {
  const { state } = useLocation<{ from: Location }>()
  const isDesktopAndBigger = useForDesktop()
  const showOSWarning = !isMac()

  const tint = gqlTintToLocalTint[content.tint]
  const getTheme = useCallback(
    (theme: DefaultTheme) => overriddenThemeFor(theme, tint),
    [tint]
  )

  return (
    <IndexLayout
      headerLink="none-with-border"
      variant={tint}
      maxContainerWidth="960px"
    >
      {state?.from && <RedirectBadge {...state} />}
      <StyledGrid gutter={gutter}>
        <StyledColumn xs={12} md={6}>
          <Title>{content.title}</Title>
          {isDesktopAndBigger && <Subtitle>{content.introduction}</Subtitle>}
          {content.features.map((feature, index) => (
            <Feature key={index}>
              <FeatureImg src={feature.icon.src} alt={feature.icon.alt} />
              <span>{feature.description}</span>
            </Feature>
          ))}
          {isDesktopAndBigger && (
            <>
              <RelatedContent content={content} />
              <HeroImage content={content} />
            </>
          )}
        </StyledColumn>
        <StyledColumn xs={12} md={6}>
          <ThemeProvider theme={getTheme}>
            <SignUpForm
              email={email}
              professionalRole={professionalRole}
              invitedUser={invitedUser}
              onCompleted={onCompleted}
              onRoleChange={onRoleChange}
              callToAction={content.callToAction}
            />
            {showOSWarning && (
              <OperatingSystemWarning data-testid="os-warning">
                To design with Sketch you&apos;ll need macOS Big Sur (11.0.0) or
                later
              </OperatingSystemWarning>
            )}
            <Line />
            <Text>
              Already have a Sketch account?{' '}
              <Link
                variant="primary"
                to={{
                  pathname: routes.SIGN_IN.create({}),
                  state,
                }}
                isUnderlined
              >
                Sign in
              </Link>
            </Text>
          </ThemeProvider>
        </StyledColumn>
      </StyledGrid>
    </IndexLayout>
  )
}

const overriddenThemeFor = (theme: DefaultTheme, tint: SidebarVariant) => {
  if (tint === 'orange') {
    return theme
  }

  const primaryColor = primaryColorFor(tint)
  const secondaryColor = transparentize(0.5, primaryColor)

  return {
    ...theme,
    colors: {
      ...theme.colors,
      sketch: {
        ...theme.colors.sketch,
        A: primaryColor,
        B: primaryColor,
        C: secondaryColor,
        E: secondaryColor,
      },
    },
  }
}

const primaryColorFor = (tint: Exclude<SidebarVariant, 'orange'>): string => {
  switch (tint) {
    case 'teal':
      return tintColors[5]
    case 'purple':
      return tintColors[8]
    case 'green':
      return tintColors[4]
    case 'beige':
      return tintColors[3]
  }
}

export default BaseSignUpView
